import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router";
import { ArrowLeft, Play, Pause, Copy, Link as LinkIcon, Layers } from "lucide-react";
import type { ComboResponse } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { api } from "@/lib/api";
import { formatDuration, formatPlayCount, cn } from "@/lib/utils";

export function ComboPermalink() {
  const { slug } = useParams<{ slug: string }>();
  const [combo, setCombo] = useState<ComboResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const { play, stop, isPlaying, playCombo, activeComboId, stopAll, comboProgress } = useAudio();

  useEffect(() => {
    if (!slug) return;
    api.combos
      .get(slug)
      .then(setCombo)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  // Preload all audio files when combo data arrives so playback is instant
  const preloadedRef = useRef(false);
  useEffect(() => {
    if (!combo || preloadedRef.current) return;
    preloadedRef.current = true;
    for (const sound of combo.sounds) {
      fetch(sound.audio_url).catch(() => {});
    }
  }, [combo]);

  const comboPlaying = combo ? activeComboId === combo.id : false;

  const handlePlayAll = () => {
    if (!combo) return;
    if (comboPlaying) {
      stopAll();
      return;
    }
    playCombo(
      combo.id,
      combo.name,
      combo.sounds.map((s) => ({
        id: s.id,
        audioUrl: s.audio_url,
        title: s.title,
        durationMs: s.duration_ms,
        slug: s.slug,
      })),
    );
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  if (!combo) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink text-parchment">
        <p className="text-lg">Combo not found</p>
        <Link to="/" className="text-spark hover:text-spark-soft">
          Back to soundboard
        </Link>
      </div>
    );
  }

  const permalink = `${window.location.origin}/c/${combo.slug}`;
  const totalDuration = combo.sounds.reduce((acc, s) => acc + s.duration_ms, 0);

  return (
    <div className="min-h-screen bg-ink">
      <div className="mx-auto max-w-xl px-3 py-4 sm:px-4 sm:py-8">
        <Link
          to="/"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-fog hover:text-parchment sm:mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to soundboard
        </Link>

        {/* Header */}
        <div className="rounded-xl border border-mist bg-slate p-4 sm:rounded-[18px] sm:p-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <button
              onClick={handlePlayAll}
              className={cn(
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors sm:h-14 sm:w-14",
                comboPlaying ? "bg-spark-soft" : "bg-spark hover:bg-spark-soft",
              )}
            >
              {comboPlaying ? (
                <Pause className="h-5 w-5 text-ink sm:h-6 sm:w-6" />
              ) : (
                <Play className="ml-0.5 h-5 w-5 text-ink sm:ml-1 sm:h-6 sm:w-6" />
              )}
            </button>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="font-display text-xl font-bold tracking-display text-parchment sm:text-2xl">
                  {combo.name}
                </h1>
                <span className="flex items-center gap-0.5 rounded-full bg-spark/12 px-2 py-0.5">
                  <Layers className="h-3 w-3 text-spark" />
                  <span className="font-mono text-[9px] font-bold uppercase tracking-wider text-spark">
                    combo
                  </span>
                </span>
              </div>

              {combo.description && (
                <p className="mt-1 text-sm text-fog">{combo.description}</p>
              )}

              <div className="mt-1.5 flex flex-wrap items-center gap-2 font-mono text-[10px] text-fog sm:mt-2 sm:gap-3 sm:text-[11px]">
                <span>{formatPlayCount(combo.play_count)} plays</span>
                <span>{combo.sounds.length} sounds</span>
                <span>{formatDuration(totalDuration)} total</span>
              </div>

              {combo.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {combo.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="rounded-full bg-slate-raised px-2 py-0.5 font-mono text-[10px] text-fog"
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Combo progress */}
          {comboPlaying && comboProgress && (
            <div className="mt-4 flex items-center gap-2">
              <div className="flex-1 overflow-hidden rounded-full bg-ink/50">
                <div
                  className="h-1 rounded-full bg-spark transition-all"
                  style={{ width: `${(comboProgress.current / comboProgress.total) * 100}%` }}
                />
              </div>
              <span className="font-mono text-[10px] text-fog">
                {comboProgress.current}/{comboProgress.total}
              </span>
            </div>
          )}
        </div>

        {/* Sound list */}
        <div className="mt-3 space-y-1 sm:mt-4">
          {combo.sounds.map((sound, i) => {
            const playing = isPlaying(sound.id);
            return (
              <div
                key={`${sound.id}-${i}`}
                className="flex items-center gap-2 rounded-xl border border-mist bg-slate px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3"
              >
                <span className="font-mono text-[11px] font-bold text-spark">{i + 1}</span>
                <button
                  onClick={() => {
                    if (playing) stop(sound.id);
                    else play(sound.id, sound.audio_url, { title: sound.title, durationMs: sound.duration_ms, slug: sound.slug });
                  }}
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
                    playing ? "bg-spark-soft" : "bg-spark hover:bg-spark-soft",
                  )}
                >
                  {playing ? (
                    <Pause className="h-3 w-3 text-ink" />
                  ) : (
                    <Play className="ml-0.5 h-3 w-3 text-ink" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <Link to={`/s/${sound.slug}`} className="truncate text-sm text-parchment hover:text-spark">
                    {sound.title}
                  </Link>
                  <span className="ml-2 font-mono text-[10px] text-fog">
                    {formatDuration(sound.duration_ms)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Share links */}
        <div className="mt-3 rounded-xl border border-mist bg-slate p-3 sm:mt-4 sm:rounded-[18px] sm:p-4">
          <h3 className="mb-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
            Share
          </h3>
          <div className="space-y-1">
            <button
              onClick={() => handleCopy(permalink, "link")}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-mist/50"
            >
              <LinkIcon className="h-3.5 w-3.5 shrink-0 text-fog" />
              <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-fog">
                {permalink}
              </span>
              {copied === "link" ? (
                <span className="font-mono text-[10px] text-moss">Copied!</span>
              ) : (
                <Copy className="h-3.5 w-3.5 shrink-0 text-fog" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
